import React, { useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
import ApprovalControl from "../approval-control/ApprovalControl";
import OfferSummaryCard, {
  type OfferSummaryCardData,
} from "./OfferSummaryCard";
import SubcontractContractPdfPreview, {
  type SubcontractPdfPreviewState,
} from "./SubcontractContractPdfPreview";

/**
 * 既存呼び出し元の互換用。新規コードは `SubcontractPdfPreviewState` を直接使うこと。
 * @deprecated SubcontractPdfPreviewState に統合済み
 */
export type OfferConfirmPdfPreview = SubcontractPdfPreviewState;

interface OfferConfirmModalProps {
  data: OfferSummaryCardData;
  /** 「戻る」押下、または閉じるボタンで呼ばれる */
  onClose: () => void;
  /** 承認 UI で確定後、「この内容で送信」押下時に呼ばれる */
  onSubmit: () => void;
  /** 送信中（API リクエスト中） */
  isSubmitting?: boolean;
  /** 送信エラーメッセージ（あれば表示） */
  errorMessage?: string | null;
  /**
   * PDF プレビューの状態。`undefined` のときは従来どおりサマリカードのみを表示する。
   * `error` のときは PDF プレビューの直下にサマリカードもフォールバック表示する。
   */
  pdfPreview?: SubcontractPdfPreviewState;
  /** PDF 生成失敗時に「再試行」ボタンから呼ばれる */
  onRetryPdf?: () => void;
}

// 承認 UI はスクロール領域の下端に sticky で固定し、フッターの真上に
// 常に表示する。Body の左右 padding を負 margin で打ち消し、上下 padding は
// 持たない（Body の padding-bottom 12px とフッターの border でフレーミング）。
const ApprovalRow = styled.div`
  position: sticky;
  bottom: 0;
  margin: 0 -12px;
  padding: 0 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  z-index: 1;
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

const OfferConfirmModal: React.FC<OfferConfirmModalProps> = ({
  data,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage,
  pdfPreview,
  onRetryPdf,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  // PDF プレビューが「無い／エラー」のときはサマリカードへフォールバックして
  // 「何を送るか」が必ず確認できる状態を保つ。生成中／表示成功時は PDF を主役にする。
  const showSummaryCard = !pdfPreview || pdfPreview.status === "error";

  return (
    <Modal
      onClose={isSubmitting ? undefined : onClose}
      title="送信内容の確認"
      description="内容を確認して承認すると、次へ進めます。"
      size="default"
      primaryAction={{
        label: isSubmitting ? "送信中..." : "この内容で送信",
        onClick: onSubmit,
        disabled: !isConfirmed || isSubmitting,
        rightIcon: "send",
      }}
      secondaryAction={{
        label: "戻る",
        onClick: onClose,
        disabled: isSubmitting,
      }}
    >
      {pdfPreview && (
        <SubcontractContractPdfPreview
          preview={pdfPreview}
          onRetry={onRetryPdf}
        />
      )}

      {showSummaryCard && <OfferSummaryCard data={data} showRecipient />}

      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

      <ApprovalRow>
        <ApprovalControl
          onConfirm={() => setIsConfirmed(true)}
          onConfirmedChange={setIsConfirmed}
          disabled={isSubmitting}
        />
      </ApprovalRow>
    </Modal>
  );
};

export default OfferConfirmModal;
